import { Link, useNavigate } from "react-router-dom";
import { MapPin, Clock, Zap, MessageCircle, Heart } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Stars, VerifiedBadge, FeaturedBadge, DemoBadge } from "./kit";
import { PaymentLabel } from "./PaymentMode";
import { api, fileUrl, errMsg } from "../lib/api";
import { useAuth, catName } from "../context/AuthContext";

export const Logo = ({ p, size = "w-14 h-14" }) =>
  p.logo ? <img src={fileUrl(p.logo)} alt={`${p.business_name} logo`} className={`${size} rounded-xl object-cover border border-slate-100`} loading="lazy" />
    : <div className={`${size} rounded-xl bg-[#EBF0F4] text-[#374B5C] grid place-items-center font-bold font-display text-lg`}>{p.business_name?.[0]}</div>;

export async function messageProvider(user, navigate, providerId) {
  if (!user) return navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
  if (user.role !== "homeowner") return toast.error("Messaging professionals is available to homeowner accounts");
  try {
    const { data } = await api.post("/conversations", { provider_id: providerId });
    navigate(`/dashboard/messages?c=${data.id}`);
  } catch (e) {
    toast.error(errMsg(e));
  }
}

export default function ProviderCard({ p, active, onHover }) {
  const { user, cats, refresh } = useAuth();
  const nav = useNavigate();
  const [saved, setSaved] = useState((user?.saved_providers || []).includes(p.id));
  const save = async () => {
    if (!user) return nav("/login");
    const { data } = await api.post(`/favourites/${p.id}`);
    setSaved(data.saved);
    refresh();
  };
  return (
    <article className={`card-hover p-5 ${active ? "ring-2 ring-[#FF8166]" : ""}`} onMouseEnter={() => onHover?.(p.id)} data-testid={`provider-card-${p.slug}`}>
      <div className="flex gap-4">
        <Logo p={p} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-1.5 mb-1">
            {p.sponsored && <FeaturedBadge label="Sponsored" />}
            {p.is_demo && <DemoBadge />}
            <VerifiedBadge levels={p.verification_levels} />
            <PaymentLabel mode={p.payment_mode} small testid="provider-card-payment" />
          </div>
          <Link to={`/professionals/${p.slug}`} className="font-display font-bold text-slate-900 hover:text-[#FF8166] transition-colors" data-testid="provider-card-name">{p.business_name}</Link>
          <div className="text-sm text-slate-500">{catName(cats, p.categories?.[0])}</div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-slate-500">
            <Stars value={p.rating} count={p.review_count} size={12} />
            <span className="inline-flex items-center gap-1"><MapPin size={12} />{p.town}{p.distance != null && ` · ${p.distance} mi`}</span>
            {p.response_time_hours && <span className="inline-flex items-center gap-1"><Clock size={12} />Replies in ~{p.response_time_hours}h</span>}
            {p.emergency?.offers && <span className="inline-flex items-center gap-1 text-rose-600 font-medium"><Zap size={12} />{p.emergency.h24 ? "24/7 emergency" : "Emergency call-outs"}</span>}
          </div>
        </div>
        <button onClick={save} aria-label={saved ? "Remove from saved" : "Save professional"} className="self-start p-2 rounded-full hover:bg-rose-50" data-testid="provider-card-save">
          <Heart size={18} className={saved ? "fill-rose-500 text-rose-500 pop" : "text-slate-400"} />
        </button>
      </div>
      <p className="text-sm text-slate-600 mt-3 line-clamp-2">{p.description}</p>
      <div className="flex flex-wrap gap-1.5 mt-3">{(p.subcategories || []).slice(0, 4).map((s) => <span key={s} className="chip bg-[#FFF1EE] text-[#D9583D]">{s}</span>)}</div>
      {p.photos?.length > 0 && <div className="flex gap-2 mt-3">{p.photos.map((u) => <img key={u} src={fileUrl(u)} alt="" loading="lazy" className="w-20 h-16 rounded-lg object-cover" />)}</div>}
      <div className="flex flex-wrap gap-2 mt-4">
        <Link to={`/professionals/${p.slug}`} className="btn-ghost btn-sm" data-testid="provider-card-view">View Profile</Link>
        <Link to={`/post-job?provider=${p.id}&category=${p.categories?.[0] || ""}`} className="btn-coral btn-sm" data-testid="provider-card-quote">Request Quote</Link>
        <button onClick={() => messageProvider(user, nav, p.id)} className="btn-ghost btn-sm" data-testid="provider-card-message"><MessageCircle size={14} />Message</button>
      </div>
    </article>
  );
}
